import { formatPercentage } from '@toggle/helpers';
import { Entity, mapEntity } from '@toggle/toggle';
import { isSameDay, isWeekend, nextMonday } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getMarketHours } from '~/components/price-display-view/helpers/price-display-helpers';
import { useFetchSnakeByName } from '~/hooks/use-fetch-snake-by-name/useFetchSnakeByName';
import { useOneDayReturnLivePrice } from '~/hooks/use-one-day-return-live-price/useOneDayReturnLivePrice';

import * as S from './company-card/CompanyCard.styles';
import { formatDateToET } from './utils';

const ONE_DAY_RETURN_SNAKE = 'mom(horiz=1d)';

interface EarningsPillProps {
  isReported: boolean;
  entity: Entity;
  reportingDate: string;
}

export const EarningsPill = ({
  isReported,
  entity,
  reportingDate,
}: EarningsPillProps) => {
  const { t } = useTranslation(['earnings']);
  const mappedEntity = useMemo(() => mapEntity(entity), [entity]);
  const { oneDayLiveReturn } = useOneDayReturnLivePrice(mappedEntity);

  /**
   * For 1D reaction, we look at the reporting date of the Earnings Call.
   * If the reporting date is pre-market, then use that date
   * If the reporting date is after market, then use the next day
   *
   * If the day we end up using is the current day (and market is open), then use the live return from the WS
   * Otherwise, use the 1d return snake data
   */

  let dateToUse: Date | undefined;

  if (reportingDate) {
    const formattedDate = new Date(reportingDate);
    const { isAfterMarket } = getMarketHours(formattedDate);

    dateToUse = isAfterMarket
      ? new Date(new Date(formattedDate).setDate(formattedDate.getDate() + 1))
      : new Date(reportingDate);

    const isWeekendDay = isWeekend(dateToUse);

    if (isWeekendDay) {
      dateToUse = nextMonday(dateToUse);
    }
  }

  const earningsDateFormatted = reportingDate
    ? formatDateToET(new Date(reportingDate))
    : undefined;

  const formattedFromDate = dateToUse
    ? new Date(
        Date.UTC(
          dateToUse.getUTCFullYear(),
          dateToUse.getUTCMonth(),
          dateToUse.getUTCDate(),
          0,
          0,
          0,
          0
        )
      ).toISOString()
    : undefined;

  const { data: snakeData } = useFetchSnakeByName(
    `${entity.tag}.${ONE_DAY_RETURN_SNAKE}`,
    undefined,
    formattedFromDate,
    !formattedFromDate
  );

  let oneDayReaction: number | undefined;

  const isToday =
    !!dateToUse && isSameDay(dateToUse, toZonedTime(new Date(), 'UTC'));

  if (isToday) {
    oneDayReaction = oneDayLiveReturn;
  } else {
    oneDayReaction = snakeData?.tsData?.find(
      item =>
        !!formattedFromDate &&
        isSameDay(new Date(item.index), new Date(formattedFromDate))
    )?.value;
  }

  const dateTitleLabel = isReported
    ? t('earnings:earningsInsight.drawer.earningsPreview.reported')
    : t('earnings:earningsInsight.drawer.earningsPreview.scheduled');

  return (
    <>
      {earningsDateFormatted && (
        <S.EarningsSection $isReported={isReported}>
          <S.EarningsDateWrapper>
            <S.EarningsDateTitleLabel>
              {dateTitleLabel}
            </S.EarningsDateTitleLabel>
            <S.EarningsDateValueLabel>
              {earningsDateFormatted}
            </S.EarningsDateValueLabel>
          </S.EarningsDateWrapper>
          {isReported && oneDayReaction !== undefined && (
            <S.EarningsReactionWrapper>
              <S.EarningsReactionTitleLabel>
                {t('earnings:earningsInsight.drawer.earningsPreview.reaction')}
              </S.EarningsReactionTitleLabel>
              <S.EarningsReactionValueLabel $isPositive={oneDayReaction > 0}>
                {formatPercentage(oneDayReaction, {
                  suffix: '%',
                  addPlus: true,
                  useOriginalValue: true,
                })}
              </S.EarningsReactionValueLabel>
            </S.EarningsReactionWrapper>
          )}
        </S.EarningsSection>
      )}
    </>
  );
};
